import type {GeneratedMediaSignals} from '@pexip/media';

import type {Subscribe} from '../types';

export const createChangeOfMediaDevicesSubscription =
    (
        signals: GeneratedMediaSignals<'onMediaChanged' | 'onDevicesChanged'>,
    ): Subscribe =>
    onChange => {
        const subscriptions = [
            signals.onMediaChanged.add(onChange),
            signals.onDevicesChanged.add(onChange),
        ];
        return () => {
            for (const unsubscribe of subscriptions) {
                unsubscribe();
            }
            subscriptions.length = 0;
        };
    };

export const createChangeOfTrackSubscription =
    (
        signals: GeneratedMediaSignals<
            'onMediaChanged' | 'onAddTrack' | 'onRemoveTrack'
        >,
        kind: 'audio' | 'video',
    ): Subscribe =>
    onChange => {
        const changeTrack = (track: MediaStreamTrack) => {
            if (track.kind === kind) {
                onChange();
            }
        };
        const unsubscribes = [
            signals.onMediaChanged.add(onChange),
            signals.onAddTrack.add(changeTrack),
            signals.onRemoveTrack.add(changeTrack),
        ];
        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            unsubscribes.length = 0;
        };
    };

/**
 * Subscribes to changes that can alter whether an input was requested,
 * including requests that complete without producing a track.
 *
 * @example
 * createRequestedMediaSubscription(signals, 'audio')(onChange);
 */
export const createRequestedMediaSubscription =
    (
        signals: GeneratedMediaSignals<
            | 'onMediaChanged'
            | 'onAddTrack'
            | 'onRemoveTrack'
            | 'onStatusChanged'
            | 'onUpdatingAudio'
            | 'onUpdatingVideo'
        >,
        kind: 'audio' | 'video',
    ): Subscribe =>
    onChange => {
        const updatingSignal =
            kind === 'audio'
                ? signals.onUpdatingAudio
                : signals.onUpdatingVideo;
        const unsubscribes = [
            createChangeOfTrackSubscription(signals, kind)(onChange),
            signals.onStatusChanged.add(onChange),
            updatingSignal.add(updating => {
                if (!updating) {
                    onChange();
                }
            }),
        ];
        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            unsubscribes.length = 0;
        };
    };

export const createMediaActiveSubscription =
    (
        signals: GeneratedMediaSignals<
            | 'onMediaChanged'
            | 'onAddTrack'
            | 'onRemoveTrack'
            | 'onMediaTrackStopped'
            | 'onMediaTrackMuted'
            | 'onMediaTrackResumed'
            | 'onMediaTrackSuspended'
        >,
    ): Subscribe =>
    onChange => {
        const unsubscribes = [
            signals.onMediaChanged.add(onChange),
            signals.onAddTrack.add(onChange),
            signals.onRemoveTrack.add(onChange),
            signals.onMediaTrackStopped.add(onChange),
            signals.onMediaTrackMuted.add(onChange),
            signals.onMediaTrackSuspended.add(onChange),
        ];
        return () => {
            for (const unsubscribe of unsubscribes) {
                unsubscribe();
            }
            unsubscribes.length = 0;
        };
    };
